import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle2, Star, ExternalLink } from "lucide-react";
import { getOptimizedImageUrl } from "../lib/utils";
import { fetchServices } from "../lib/cms";

export default function Services() {
  const defaultServices = [
    {
      title: "Totalrenovering",
      slug: "totalrenovering",
      desc: "Vi forvandler ældre boliger til moderne drømmehjem med respekt for den oprindelige stil. Søren Sehested har over 20 års erfaring med renovering og står selv for alt arbejdet fra start til slut.",
      image: "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00005-scaled.jpeg"
    },
    {
      title: "Ombygning",
      slug: "ombygning",
      desc: "Skab mere lys, luft og plads med gennemtænkte ombygninger og loft til kip. Vi udfører alt fra mindre rumændringer til store, komplekse ombygningsprojekter.",
      image: "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00006-2-scaled.jpeg"
    },
    {
      title: "Totalentreprise",
      slug: "totalentreprise",
      desc: "Få totalentreprise i Aarhus med én erfaren tømrer som fast kontakt. Vi styrer dit byggeprojekt trygt fra idé til færdigt resultat – med solidt håndværk og færre overdragelser.",
      image: "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00003-scaled.jpeg"
    },
    {
      title: "Tilbygninger",
      slug: "tilbygninger",
      desc: "Mangler du plads? Vi bygger harmoniske tilbygninger, der passer perfekt til din eksisterende bolig og skaber mærkbare kvadratmeter.",
      image: "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00005-3-scaled.jpeg"
    },
    {
      title: "Træterrasser",
      slug: "traeterrasser",
      desc: "Få en udendørs oase med en skræddersyet træterrasse i høj kvalitet. Vi designer og bygger terrasser, der passer til din bolig og have.",
      image: "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00001-scaled.jpeg"
    },
    {
      title: "Døre og vinduer",
      slug: "doere-og-vinduer",
      desc: "Udskiftning af døre og vinduer til energivenlige og stilfulde løsninger. Vi sikrer præcis montering og bedre indeklima.",
      image: "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00012-scaled.jpeg"
    },
    {
      title: "Tagarbejde",
      slug: "tagarbejde",
      desc: "Nyt tag eller tagrenovering? Vi udfører professionelt tagarbejde, der beskytter dit hjem mod vind og vejr i mange år fremover.",
      image: "https://assets.auxodev.dk/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00006-scaled.jpeg"
    }
  ];

  const [services, setServices] = useState(defaultServices);

  useEffect(() => {
    fetchServices().then((cmsServices) => {
      if (cmsServices && cmsServices.length > 0) {
        setServices(cmsServices);
      }
    });
  }, []);

  const metaTitle = "Vores Ydelser - Sehested Tømrerfirma ApS";
  const metaDesc = "Sehested Tømrerfirma ApS tilbyder totalrenovering, ombygning, totalentreprise, tilbygning, træterrasser, døre, vinduer og tagarbejde i Aarhus Syd.";

  return (
    <div className="py-12 md:py-20 bg-primary-50/30">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
      </Helmet>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <span className="text-sm font-bold uppercase tracking-widest text-brand-600">Hvad vi tilbyder</span>
          <h1 className="font-display text-4xl font-bold tracking-tight text-primary-950 sm:text-5xl">
            Vores Ydelser
          </h1>
          <p className="text-primary-600 text-lg">
            Vi leverer solidt håndværk og skræddersyede løsninger til både små og store opgaver – altid med personlig kontakt og høj kvalitet i fokus.
          </p>
        </div>

        {/* Services List */}
        <div className="space-y-16">
          {services.map((service, index) => (
            <div
              key={service.slug}
              className={`flex flex-col lg:flex-row gap-8 lg:gap-12 items-center ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Image */}
              <div className="w-full lg:w-1/2">
                <div className="aspect-[16/10] overflow-hidden rounded-2xl border border-primary-200 shadow-sm">
                  <img
                    src={getOptimizedImageUrl(service.image, { width: 600, height: 375 })}
                    alt={service.title}
                    className="h-full w-full object-cover hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="w-full lg:w-1/2 space-y-6">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-primary-950">
                  {service.title}
                </h2>
                <p className="text-primary-700 leading-relaxed">
                  {service.desc}
                </p>
                <div className="flex flex-wrap gap-4 pt-2">
                  <Link
                    to={
                      service.slug === "totalrenovering"
                        ? "/totalrenovering"
                        : service.slug === "ombygning"
                        ? "/ombygning"
                        : service.slug === "totalentreprise" || service.slug === "totalentrepriser"
                        ? "/totalentreprise"
                        : `/ydelser/${service.slug}`
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-primary-900 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-600 transition-all duration-200"
                  >
                    Læs mere om {service.title.toLowerCase()}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Google Reviews Callout Banner on /ydelser */}
        <div className="mt-20 overflow-hidden rounded-2xl border border-primary-200/90 bg-gradient-to-br from-white via-primary-50/40 to-amber-50/30 p-6 sm:p-10 shadow-xs">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div className="space-y-4 max-w-xl">
              <div className="flex items-center gap-3">
                {/* Official Google Multi-color G logo */}
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-xs border border-primary-100 p-2.5 shrink-0">
                  <svg className="h-full w-full" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z" />
                    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                    <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.16 0 9.98 0 12s.45 3.84 1.25 5.42l4.03-3.15z" />
                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                  </svg>
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-bold text-primary-950">Google Anmeldelser</span>
                  </div>
                  <p className="text-xs text-primary-500 font-medium">Sehested Tømrerfirma • Hasselager & Aarhus</p>
                </div>
              </div>

              <div>
                <h3 className="font-display text-2xl font-bold text-primary-950 sm:text-3xl">
                  Har vi hjulpet dig med et bygge- eller renoveringsprojekt?
                </h3>
                <p className="mt-2.5 text-sm sm:text-base text-primary-700 leading-relaxed">
                  Din mening gør en forskel! Har du fået udført tømrerarbejde, renovering eller ombygning af Søren Sehested, sætter vi stor pris på, hvis du vil dele din oplevelse med en hurtig anmeldelse på Google.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-y-1.5 gap-x-5 text-xs text-primary-600 pt-1">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" /> Tager under 1 minut
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" /> Direkte på Google
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" /> Hjælper lokale boligejere
                </span>
              </div>
            </div>

            <div className="shrink-0 flex flex-col sm:flex-row md:flex-col items-stretch sm:items-center md:items-stretch gap-2.5">
              <a
                href="https://g.page/r/CS5RL2i4p0k0EBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 rounded-full bg-primary-950 hover:bg-brand-600 px-7 py-4 text-xs sm:text-sm font-bold text-white transition-all duration-200 shadow-sm group cursor-pointer"
              >
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z" />
                  <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                  <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.16 0 9.98 0 12s.45 3.84 1.25 5.42l4.03-3.15z" />
                  <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                </svg>
                <span>Giv en Google anmeldelse</span>
                <ExternalLink className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
              </a>
              <p className="text-center text-[11px] text-primary-500">
                Åbner direkte i Google Reviews
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
